import React, {
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { DescriptionMiniInput, MiniInput } from 'shared/ui';
import {
  PhoneConfirmationInput,
  type PhoneConfirmationInputRef,
} from 'features';
import { QrCodVkBotSvg } from 'shared/svg';
import styles from './step-customer-info.module.scss';

interface IStepCustomerInfo {
  name: string;
  phone: string;
  comment: string;
  onNameChange: (name: string) => void;
  onPhoneChange: (phone: string) => void;
  onCommentChange: (comment: string) => void;
}

export interface StepCustomerInfoRef {
  validate: () => boolean;
}

export const StepCustomerInfo = forwardRef<
  StepCustomerInfoRef,
  IStepCustomerInfo
>(
  (
    { name, phone, comment, onNameChange, onPhoneChange, onCommentChange },
    ref
  ) => {
    const [nameError, setNameError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [, setIsPhoneVerified] = useState(false);
    const phoneConfirmRef = useRef<PhoneConfirmationInputRef>(null);

    useImperativeHandle(ref, () => ({
      validate: () => {
        let valid = true;
        if (!name.trim()) {
          setNameError('Обязательное поле');
          valid = false;
        }
        if (!phone || phone.length < 11) {
          setPhoneError('Введите корректный номер');
          valid = false;
        }
        if (
          phoneConfirmRef.current?.isVkChecked &&
          phoneConfirmRef.current?.hasVkLink &&
          !phoneConfirmRef.current?.isVerified
        ) {
          setPhoneError('Подтвердите номер телефона');
          valid = false;
        }
        return valid;
      },
    }));

    const handleNameChange = (value: string) => {
      setNameError('');
      onNameChange(value);
    };

    const handlePhoneVerified = (verifiedPhone: string) => {
      onPhoneChange(verifiedPhone);
      setIsPhoneVerified(true);
      setPhoneError('');
    };

    return (
      <div className={styles.form__container}>
        <div className={styles.form__field}>
          <span className={styles.form__label}>Имя *</span>
          <MiniInput
            inputValue={name}
            inputPlaceholder="Имя"
            onChange={handleNameChange}
            className={styles.input_padding}
          />
          {nameError && <span className={styles.form__error}>{nameError}</span>}
        </div>
        <div className={styles.form__field}>
          <span className={styles.form__label}>Номер телефона *</span>
          <PhoneConfirmationInput
            ref={phoneConfirmRef}
            onVerified={handlePhoneVerified}
            onPhoneChange={onPhoneChange}
          />
          {phoneError && (
            <span className={styles.form__error}>{phoneError}</span>
          )}
        </div>
        <div className={styles.form__field}>
          <DescriptionMiniInput
            inputValue={comment}
            inputTitle="Комментарий к записи"
            inputPlaceholder="Комментарий... (до 80 символов)"
            onChange={onCommentChange}
          />
        </div>
        <div className={styles.form__qr}>
          <span className={styles.form__qr_title}>Наш VK бот</span>
          <QrCodVkBotSvg />
        </div>
      </div>
    );
  }
);
